export const ONBOARDING_PAGES = {
    LANDING: '/onboarding',
    PAIR: '/onboarding/pair',
    SELECT_BRANCH: '/onboarding/select-branch',
    SWITCH_BRANCH: '/onboarding/switch-branch',
    STAFF_LOGIN: '/onboarding/login',
    PIN_UPGRADE_SIGNED_OUT: '/onboarding/pin-upgrade-signed-out',
} as const;

export const CUSTOMER_PAGES = {
    LIST: '/register/customers',
    CREATE: '/register/customers/create',
    VIEW_CUSTOMER: (customerId: string) =>
        `/register/customers?customerId=${customerId}`,
    VIEW_CUSTOMER_OUTSTANDING_SALE: (customerId: string) =>
        `/register/customers?currentTab=outstanding_sales&customerId=${customerId}`,
} as const;

export const SALES_PAGES = {
    COMPLETED: '/register/sales/completed',
    OUTSTANDING: '/register/sales/outstanding',
    UNSYNCED: '/register/sales/unsynced',
    REFUNDS: '/register/sales/refunds',
};

export const REPORTS_PAGES = {
    SALE_SUMMARY: '/register/reports/summary',
    SALE_BY_PAYMENT_METHOD: '/register/reports/sales-by-payment-method',
    SALE_BY_ITEM: '/register/reports/sales-by-item',
    SALE_BY_CATEGORY: '/register/reports/sales-by-category',
};

const TERMINAL_PAGES = {
    LIST: '/register/terminals',
    CONNECT: '/register/terminals/connect',
};

const PRINTER_PAGES = {
    LIST: '/register/printers',
    ADD: '/register/printers/add',
};

export const PAGES = {
    HOME: '/register/sell',
    SELL: '/register/sell',
    CUSTOMER: CUSTOMER_PAGES,
    SAVED_CART: '/register/saved-carts',
    CHECKOUT: '/register/sell/checkout',
    WALLET_TOP_UP: '/register/wallet/topup',
    ONBOARDING: ONBOARDING_PAGES,
    TERMINAL: TERMINAL_PAGES,
    PRINTER: PRINTER_PAGES,
    SALES: SALES_PAGES,
    REPORTS: REPORTS_PAGES,
    TRANSFERS: '/register/transfers',
};

export const REGISTERS_API_URL = {
    REGISTER: {
        UNPAIR: '/register/api/v1/register/unpair',
        PAIR: 'register/api/v1/register/pair',
        GET_BRANCH_TOKEN: 'register/api/v1/register/pair/branch-token',
        USER: '/register/api/v2/register/user',
        AUTO_SWITCH_BRANCH: 'register/api/v1/register/switch-branch/auto',
        CONFIGURATION: '/register/api/v1/register/configuration',
        INFO: '/register/api/v1/register/info',
        DOWNLOAD_URLS: 'register/api/v1/app-version/download-urls',
        PAYMENT_STATUS: 'register/api/v1/register-payment/payments/status',
        VALIDATE_TRANSACTION_REFERENCE: (reference: string) =>
            `register/api/v1/register-payment/transaction-reference/${reference}/validate`,
        VALIDATE_TRANSACTION_REFERENCES:
            'register/api/v1/register-payment/transaction-references/validate',
        TERMINALS: 'register/api/v1/register/terminals',
        BRANCHES: 'register/api/v1/register/branch/list',
        GET_ASSIGNED_TERMINALS:
            '/register/api/v1/register/terminals?assigned=true',
        CREATE_TERMINAL_PAYMENT:
            'register/api/v1/register-payment/terminal-payments',
        CREATE_SAVED_CART: '/order/api/v1/cart',
        SAVED_CART: '/order/api/v1/cart',
        CART_DETAILS: '/order/api/v2/cart',
        DATE_TIME: '/register/api/v1/app-version/utc-datetime',
        PAYMENT_RESTRICTIONS: '/register/api/v2/register/payment-group',
        DOCKETS: '/register/api/v1/register/dockets',

        CART_UPDATE_V2: '/order/api/v3/cart',
        BUSINESS_PROPERTY: '/business/api/v1/business-property',
        REGISTER_PAYMENT_METHOD_LIST: '/order/api/v1/payment-method',
    },
    TERMINAL: {
        INITIATE_PAYMENT: '/register/api/v1/terminal/payment/initiate',
        CONFIRM_PAYMENT: (paymentId: string) =>
            `/register/api/v1/terminal/payment/${paymentId}`,
        LIST_CARD_TRANSACTIONS:
            '/collection/api/v1/register/cba-transactions/by-card',
    },

    PRODUCT: {
        ITEMS: '/product/api/v1/register/items',
        SEARCH: '/product/api/v1/register/items/search',
        ITEM_STOCKS: '/product/api/v1/register/item-stocks',
        MEASUREMENT_UNITS: '/product/api/v1/register/measurement-units',
        PRICING_GROUPS: '/product/api/v1/register/pricing-groups',
        MODIFIERS: '/product/api/v1/register/modifier-groups',
        CATEGORIES: '/product/api/v1/register/categories',
        DISCOUNTS: '/product/api/v1/register/discounts',
    },

    SALE: {
        RECORD: '/order/api/v1/register/sale',
        REPRINT_RECEIPT: '/order/api/v1/register/sale/reprint-receipt',
        GET_SALE: '/order/api/v1/sale',
        WALLET_CHANGE: '/order/api/v1/register/sale',
        DOWNLOAD_INVOICE: '/order/api/v1/register/sale/invoice-download',
        OUTSTANDING_PAYMENT_SUMMARY:
            '/order/api/v2/sale/outstanding/payment-summary',
        BULK_PAYMENTS: '/order/api/v2/sale/outstanding/bulk-payments',
    },

    CUSTOMER: {
        LIST: '/customer/api/v1/register/customer',
        LEDGER: '/customer/api/v1/register/ledger',
        CREATE_TRANSACTION: (ledgerId: string) =>
            `/customer/api/v1/register/ledger/${ledgerId}/transaction`,
        LOYALTY_LEDGERS: (customerId: string) =>
            `/customer/api/v1/register/customer/${customerId}/loyalty-ledgers`,
        LOYALTY_TRANSACTION: (loyaltyLedgerId: string) =>
            `/customer/api/v1/register/loyalty/ledger/${loyaltyLedgerId}/transaction`,
    },

    SUBSCRIPTION: {
        STATUS: '/subscription/api/v1/register/subscription/status',
    },

    REFUND: {
        CREATE: (saleId: string) =>
            `/order/api/v1/register/sale/${saleId}/refunds`,
        REFUNDABLE_ITEMS: (saleId: string) =>
            `/order/api/v1/register/sale/${saleId}/available-items`,
    },

    RETURNS: {
        CREATE: (saleId: string) =>
            `/order/api/v1/register/sale/${saleId}/outstanding-sale/returns`,
    },

    ACCOUNT: {
        GET_REGISTER_COLLECTION_ACCOUNT:
            '/collection/api/v1/register/collection-account',
        GET_ACCOUNT_TRANSACTION_HISTORY: (accountId: string) =>
            `/collection/api/v1/transaction/collection-account/${accountId}`,
        GET_ACCOUNT_TRANSACTION_DETAILS: (transactionId: string) =>
            `/collection/api/v1/transaction/${transactionId}`,
        RESERVE_TRANSACTION: (transactionId: string) =>
            `/collection/api/v1/register/transaction/${transactionId}/reserve`,
        LIST_TRANSFER_TRANSACTIONS:
            '/collection/api/v1/register/cba-transactions/by-transfer',
    },

    REPORT: {
        GET_SALES_BY_PAYMENT_METHOD: '/order/api/v1/payment-method/sales',
        GET_SALES_SUMMARY: '/reporting/api/v1/business/sales',
        GET_SALES_SUMMARY_V2: '/reporting/api/v2/business/sales',
        GET_SALES_BY_ITEM: '/reporting/api/v1/item/sales',
        GET_SALES_BY_ITEM_V2: '/reporting/api/v2/item/sales',
        GET_SALES_BY_CATEGORY: '/reporting/api/v1/category/sales',
        GET_SALES_BY_CATEGORY_V2: '/reporting/api/v2/category/sales',
    },
} as const;

export const DIGIT_PLACEHOLDER = '9';

export const NAIRA_SYMBOL = '₦';
export const PERCENTAGE_SYMBOL = '%';

export const APP_BACKGROUND_JOBS = {
    SALES_SYNC: 'sales-sync',
    REGISTER_INFO_LOG: 'register-info-log',
    SUBSCRIPTION: 'subscription',
    CART_SYNC: 'saved-cart-sync',
    CART_FETCH: 'remote-cart-fetch',
    ITEM_STOCK_SYNC: 'item-stock-sync',
} as const;

export type AppBackgroundJobs =
    (typeof APP_BACKGROUND_JOBS)[keyof typeof APP_BACKGROUND_JOBS];

export const REPORTS_FILTER_KEYS = {
    DATE_FROM: 'dateFrom',
    DATE_TO: 'dateTo',
    STAFF: 'staff',
    STAFFID: 'staffId',
    TIME_FROM: 'timeFrom',
    TIME_TO: 'timeTo',
    BRANCHES: 'branches',
    CHART_TYPE: 'chartType',
    GROUP_BY: 'groupBy',
    SALE_TYPE: 'saleType',
    REGISTER: 'registers',
};

export const ORDERS_FILTER_KEYS = {
    DATE_FROM: 'saleDateFrom',
    DATE_TO: 'saleDateTo',
    DUE_DATE_FROM: 'dueDateFrom',
    DUE_DATE_TO: 'dueDateTo',
    STAFF: 'staff',
    BRANCHES: 'branches',
    REGISTER: 'register',
    PAGE: 'page',
    SIZE: 'pageSize',
    PAYMENT_STATUS: 'paymentStatus',
    CUSTOMERS: 'customers',
};

export const ORDERS_BASE_FILTER_KEYS = [
    ORDERS_FILTER_KEYS.DATE_FROM,
    ORDERS_FILTER_KEYS.DATE_TO,
    ORDERS_FILTER_KEYS.STAFF,
    ORDERS_FILTER_KEYS.BRANCHES,
    ORDERS_FILTER_KEYS.REGISTER,
    ORDERS_FILTER_KEYS.PAGE,
    ORDERS_FILTER_KEYS.SIZE,
    ORDERS_FILTER_KEYS.PAYMENT_STATUS,
    ORDERS_FILTER_KEYS.CUSTOMERS,
];

export const REPORTS_BASE_FILTER_KEYS = [
    REPORTS_FILTER_KEYS.DATE_FROM,
    REPORTS_FILTER_KEYS.DATE_TO,
    REPORTS_FILTER_KEYS.TIME_FROM,
    REPORTS_FILTER_KEYS.TIME_TO,
    REPORTS_FILTER_KEYS.STAFF,
    REPORTS_FILTER_KEYS.BRANCHES,
    REPORTS_FILTER_KEYS.CHART_TYPE,
    REPORTS_FILTER_KEYS.GROUP_BY,
    REPORTS_FILTER_KEYS.SALE_TYPE,
    REPORTS_FILTER_KEYS.STAFFID,
    REPORTS_FILTER_KEYS.REGISTER,
];

export const LOGGER_EVENT_KEYS = {
    SEARCH: {
        INITIALIZE_ERROR: 'minisearch-init-error',
        WORKER_ERROR: 'minisearch-worker-error',
        WORKER_REGISTERED: 'minisearch-worker-registered',
        WORKER_TERMINATED: 'minisearch-worker-terminated',
        HYDRATE_SUCCESS: 'minisearch-hydrate-success',
        HYDRATE_ERROR: 'minisearch-hydrate-error',
        HYDRATE_MISMATCH: 'minisearch-hydrate-mismatch',
        BUILD_STARTED: 'minisearch-build-started',
        BUILD_SUCCESS: 'minisearch-build-success',
        BUILD_ERROR: 'minisearch-build-error',
        INDEX_CLEARED: 'minisearch-index-cleared',
        CLEAR_ERROR: 'minisearch-clear-error',
        UPSERT_ERROR: 'minisearch-upsert-error',
        UPSERT_SUCCESS: 'minisearch-upsert-success',
        INDICES_REMOVED: 'minisearch-indices-removed',
        QUERY_ERROR: 'minisearch-query-error',
        QUERY_PERFORMANCE: 'minisearch-query-performance',
        QUERY_EMPTY: 'minisearch-query-empty',
        QUERY_SLOW: 'minisearch-query-slow',
        REQUEST_TIMEOUT: 'minisearch-request-timeout',
        RESPONSE_RECEIVED: 'minisearch-response-received',
    },
};

export const SEARCH_TERMINATE_REASON = {
    UNPAIR: 'unpair',
    INIT_FAILED: 'init-failed',
} as const;

export const SEARCH_CLEAR_REASON = {
    FULL_RESYNC: 'full-resync',
} as const;

export const MINIMUM_MEASURABLE_QUANTITY = 1;
export const DECIMAL_PRECISION_BASE = 10;

export const EVERY_MINUTE = 60 * 1000;
export const EVERY_DAY = 24 * 60 * EVERY_MINUTE;

export const DEFAULT_NOTE_LIMIT = 100;
export const EXTENDED_NOTE_LIMIT = 300;

export const MAX_DISCOUNT_SIZE = 5;

export const APP_VERSION_KEY = 'mb_register_version';

export const MONTH_DAY_YEAR_FORMAT = 'MMMM D, YYYY';

export const API_PARAM_OFFSET_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const API_PARAM_OFFSET_TIME_FORMAT = 'HH:mm:ssZ';

export const FREE_MODIFIER_LABEL = 'FREE';
